import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type ColumnOrderState,
  type VisibilityState,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Page } from "../../components/Page";
import {
  useCustomerList,
  CUSTOMER_LIST_QUERY_KEY,
} from "../../helpers/hooks/use-customer-list";
import { PORTFOLIO_OVERVIEW_QUERY_KEY } from "../../helpers/hooks/use-portfolio-overview";
import type {
  CustomerRow,
  HealthBand,
  CustomerListResponse,
} from "../../types/crm";
import { bulkTestFlag } from "../../helpers/api/crm";
import {
  crmColumns,
  type CrmTableMeta,
  DEFAULT_COLUMN_ORDER,
  PRIMARY_COLUMN_IDS,
  MARKETING_COLUMN_IDS,
  PRICING_COLUMN_IDS,
  BILLING_COLUMN_IDS,
  ENGAGEMENT_COLUMN_IDS,
  isSectionBoundary,
  REQUIRED_VISIBLE_COLUMN_IDS,
  LEFT_ALIGNED_COLUMN_IDS,
  CENTER_ALIGNED_COLUMN_IDS,
} from "./columns";
import { PortfolioStatCards } from "./PortfolioStatCards";
import { Switch } from "../../components/Switch";
import { Download } from "lucide-react";
import { exportCustomersToCsv } from "./exportCustomersToCsv";

function alignmentClass(colId: string): string {
  if (LEFT_ALIGNED_COLUMN_IDS.has(colId)) return "text-left";
  if (CENTER_ALIGNED_COLUMN_IDS.has(colId)) return "text-center";
  return "text-right";
}

// Row tint by health band. Uses solid Tailwind palette tones (NOT
// brand-primary/10 with opacity) so sticky # / Hotel cells fully occlude
// content scrolling behind them — opacity-based tints let horizontally-
// scrolled section columns bleed through the sticky pair.
const ROW_TINT: Record<HealthBand, string> = {
  healthy: "bg-[#e6f4f7]",
  watch: "bg-amber-50",
  concerning: "bg-orange-50",
  at_risk: "bg-[#fbe9ed]",
};
const STICKY_TINT: Record<HealthBand, string> = ROW_TINT;

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

// Versioned localStorage key for the user's column order. Bump whenever
// the canonical default order changes meaningfully so existing users get
// the new layout instead of a stale cached one. Latest bump (v10) drops a
// duplicate `revenue30d` from Primary; the column lives only in Marketing.
const LS_COL_ORDER_KEY = "crm.customerList.columnOrder.v10";
const LS_ACV_MODE_KEY = "crm.customerList.acvMode";
const LS_COL_VISIBILITY_KEY = "crm.customerList.columnVisibility.v3";

/**
 * Columns hidden by default — surfaced via the Columns popover but not
 * shown in the table on first load. CSMs use these only occasionally
 * (Owner is set in the 360 page; Test is admin-only QA; Revenue 30d
 * duplicates info already visible in MRR/Profit).
 */
const DEFAULT_HIDDEN_COLUMN_IDS: ReadonlyArray<string> = [
  "owners",
  "testFlag",
  "revenue30d",
];

type AcvMode = "default" | "avg3";

/** Section keys used by the "Jump to" scroll chips — every column is
 *  always visible; these just jump horizontally. */
type VisibleSections =
  | "primary"
  | "marketing"
  | "pricing"
  | "billing"
  | "engagement";

function loadAcvMode(): AcvMode {
  try {
    const raw = localStorage.getItem(LS_ACV_MODE_KEY);
    if (raw === "default" || raw === "avg3") return raw;
  } catch {
    /* ignore */
  }
  return "default";
}

function saveAcvMode(mode: AcvMode) {
  try {
    localStorage.setItem(LS_ACV_MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Reconcile the cached column order with the current default order so new
 * columns introduced after the user first loaded the dashboard still
 * appear. Missing IDs from the cached list are appended at the end in
 * their default-order position; stale IDs (columns that no longer exist)
 * are dropped. Without this, a user who saved their column order before
 * `revenue30d` / any future column was added would never see that column
 * render — it wouldn't be in their cached order, so TanStack Table would
 * skip it.
 */
function loadColumnOrder(): ColumnOrderState {
  try {
    const raw = localStorage.getItem(LS_COL_ORDER_KEY);
    if (!raw) return DEFAULT_COLUMN_ORDER;
    const cached = JSON.parse(raw) as ColumnOrderState;
    const cachedSet = new Set(cached);
    const knownSet = new Set(DEFAULT_COLUMN_ORDER);
    // Keep the user's preferred ordering for columns they've seen, then
    // append any new columns from the default at the end.
    const stillValid = cached.filter((id) => knownSet.has(id));
    const newlyAdded = DEFAULT_COLUMN_ORDER.filter(
      (id) => !cachedSet.has(id),
    );
    return [...stillValid, ...newlyAdded];
  } catch {
    return DEFAULT_COLUMN_ORDER;
  }
}

function saveColumnOrder(order: ColumnOrderState) {
  try {
    localStorage.setItem(LS_COL_ORDER_KEY, JSON.stringify(order));
  } catch {
    // ignore
  }
}

function defaultVisibility(): VisibilityState {
  // Start everything visible, then hide the small "rarely needed by
  // default" set per directive 2026-04-27 (Owner, Test, Revenue 30d).
  const out: VisibilityState = {};
  for (const id of DEFAULT_HIDDEN_COLUMN_IDS) out[id] = false;
  return out;
}

function loadColumnVisibility(): VisibilityState {
  try {
    const raw = localStorage.getItem(LS_COL_VISIBILITY_KEY);
    if (!raw) return defaultVisibility();
    const parsed = JSON.parse(raw) as VisibilityState;
    // Force required columns visible regardless of stale persisted state.
    for (const id of REQUIRED_VISIBLE_COLUMN_IDS) parsed[id] = true;
    return parsed;
  } catch {
    return defaultVisibility();
  }
}

function saveColumnVisibility(state: VisibilityState) {
  try {
    localStorage.setItem(LS_COL_VISIBILITY_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function TableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`sk-${r}`} className="animate-pulse">
          {Array.from({ length: cols }).map((_, c) => (
            <td
              key={`sk-${r}-${c}`}
              className="px-4 py-3 border-b border-b-border-primary"
            >
              <div className="w-full h-4 bg-surface-disabled rounded-md" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sort icon
// ---------------------------------------------------------------------------

function SortIcon({ dir }: { dir: "asc" | "desc" | false }) {
  if (!dir) return <span className="inline-block w-3 opacity-30">⇅</span>;
  return (
    <span className="inline-block w-3">
      {dir === "asc" ? "↑" : "↓"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Columns menu — toggle visibility, grouped by section
// ---------------------------------------------------------------------------

const SECTION_LABELS: Record<string, string> = {
  primary: "Primary",
  marketing: "Marketing",
  pricing: "Pricing",
  billing: "Billing",
  engagement: "Engagement",
};

const COLUMN_LABELS: Record<string, string> = {
  rowNumber: "#",
  hotelName: "Hotel",
  testFlag: "Test",
  plan: "Plan",
  products: "Product",
  monthlyRevenue: "MRR",
  spend30d: "Ad Spend",
  margin30d: "Profit",
  carr: "CARR",
  acvArr: "ACV/ARR",
  health: "Health",
  lastCharge: "Last Charge",
  productType: "Type",
  pms: "PMS",
  revenue30d: "Revenue (30d)",
  marginTrend90d: "Profit Trend (90d)",
  owners: "Owner",
  fixedFeeMonthly: "Fixed Fee/mo",
  churnRisk: "Churn Risk",
  lastActive: "Last Log In",
  needsReplyCount: "Needs Reply",
  openTicketCount: "Open Tickets",
  daysToRenewal: "Days to Renewal",
  nextAction: "Next Action",
  note: "Notes",
  impressions30d: "Impressions (30d)",
  impressions7d: "Impressions (7d)",
  impressionsWowDelta: "Impressions WoW",
  clicks30d: "Clicks",
  ctr: "CTR",
  bookings30d: "Bookings",
  bookingsValueUsd30d: "Bookings $",
  cac: "CAC",
  autumnShareOfTraffic: "Traffic Share",
  autumnShareOfBookings: "Booking Share",
  brandProtectionPct: "Brand Protection %",
  brandProtectionBookings30d: "BP Bookings",
  nonBrandProtectionBookings30d: "Non-BP Bookings",
  bookingsTrendDelta: "Trend Δ",
  spendPacing: "Spend Pacing",
  pricingGoLive: "Pricing Go-Live",
  daysOnPricing: "Days on Pricing",
  occupancyLift30d: "Occupancy Δ",
  adrChange30d: "ADR Δ",
  revParImprovement30d: "RevPAR Δ",
  revParYoY: "RevPAR YoY",
  adrYoY: "ADR YoY",
  occupancyYoY: "Occupancy YoY",
  revParSinceGoLive: "RevPAR Since Go-Live",
  revenueLiftSinceGoLiveUsd: "Revenue Lift $",
  commissionRate: "Commission %",
  totalCommissionYtd: "Commission YTD",
  lastCommissionBill: "Last Commission",
  chargeCountAllTime: "Charge Count",
  paymentMethodStatus: "Card Status",
  signedDate: "Signed",
  contractEndDate: "Contract End",
  tenureMonths: "Tenure",
  createdAt: "Created",
  daysSinceLastEmail: "Days Since Email",
  lastProactiveTouchAt: "Last Proactive Touch",
  ticketsThisMonth: "Tickets (mo)",
  dashboardUnlockedAt: "Dashboard Unlocked",
  inAppRating: "In-App Rating",
};

const SECTION_ORDER: ReadonlyArray<keyof typeof SECTION_LABELS> = [
  "primary",
  "marketing",
  "pricing",
  "billing",
  "engagement",
];

interface ColumnsMenuProps {
  table: ReturnType<typeof useReactTable<CustomerRow>>;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function ColumnsMenu({ table, open, onToggle, onClose }: ColumnsMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const groups: Record<string, typeof table.getAllLeafColumns> = {} as never;
  const grouped: Record<string, Array<ReturnType<typeof table.getAllLeafColumns>[number]>> = {
    primary: [],
    marketing: [],
    pricing: [],
    billing: [],
    engagement: [],
    other: [],
  };
  for (const col of table.getAllLeafColumns()) {
    const section = SECTION_BY_COL_ID[col.id] ?? "other";
    grouped[section].push(col);
  }

  const hiddenCount = table
    .getAllLeafColumns()
    .filter((c) => !c.getIsVisible()).length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          "text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5",
          open
            ? "bg-brand-primary/10 text-brand-primary border-brand-primary/30"
            : "bg-surface-primary text-content-secondary border-[#E9E9EB] hover:bg-surface-hover",
        )}
        title="Show or hide table columns"
      >
        <svg
          className="w-3.5 h-3.5"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <rect x="2" y="3" width="3" height="10" rx="0.5" />
          <rect x="6.5" y="3" width="3" height="10" rx="0.5" />
          <rect x="11" y="3" width="3" height="10" rx="0.5" />
        </svg>
        Columns
        {hiddenCount > 0 && (
          <span className="text-[10px] bg-content-tertiary/20 text-content-secondary rounded-full px-1.5">
            {hiddenCount} hidden
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 z-30 bg-surface-primary border border-border-primary rounded-lg shadow-modal w-80 max-h-[60vh] overflow-y-auto">
          <div className="px-3 py-2 border-b border-border-primary flex items-center justify-between">
            <span className="text-xs font-medium text-content-primary">
              Columns
            </span>
            <button
              type="button"
              onClick={() =>
                table.getAllLeafColumns().forEach((c) => c.toggleVisibility(true))
              }
              className="text-xs text-brand-primary hover:underline"
            >
              Show all
            </button>
          </div>
          {SECTION_ORDER.map((sectionKey) => {
            const cols = grouped[sectionKey];
            if (!cols.length) return null;
            // Per-section bulk-toggle state. Required columns (rowNumber,
            // hotelName) are excluded from the bulk action — they're always
            // visible and shouldn't be flipped.
            const toggleableCols = cols.filter(
              (c) => !REQUIRED_VISIBLE_COLUMN_IDS.includes(c.id),
            );
            const visibleCount = toggleableCols.filter((c) =>
              c.getIsVisible(),
            ).length;
            const hiddenInSection = toggleableCols.length - visibleCount;
            const allVisible =
              toggleableCols.length > 0 && hiddenInSection === 0;
            const sectionAction = allVisible ? "Hide all" : "Show all";
            const onToggleSection = () => {
              const next = !allVisible;
              for (const c of toggleableCols) c.toggleVisibility(next);
            };
            return (
              <div key={sectionKey} className="py-2">
                <div className="px-3 pb-1 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wide text-content-tertiary font-medium inline-flex items-center gap-1.5">
                    {SECTION_LABELS[sectionKey]}
                    {hiddenInSection > 0 && (
                      <span className="text-[10px] bg-content-tertiary/15 text-content-secondary rounded-full px-1.5 py-px normal-case font-normal tracking-normal">
                        {hiddenInSection} hidden
                      </span>
                    )}
                  </span>
                  {toggleableCols.length > 0 && (
                    <button
                      type="button"
                      onClick={onToggleSection}
                      className="text-[10px] text-brand-primary hover:underline normal-case font-normal tracking-normal"
                    >
                      {sectionAction}
                    </button>
                  )}
                </div>
                {cols.map((col) => {
                  const required = REQUIRED_VISIBLE_COLUMN_IDS.includes(col.id);
                  const label = COLUMN_LABELS[col.id] ?? col.id;
                  return (
                    <label
                      key={col.id}
                      className={clsx(
                        "flex items-center gap-2 px-3 py-1 text-sm",
                        required
                          ? "text-content-tertiary cursor-not-allowed"
                          : "text-content-primary cursor-pointer hover:bg-surface-hover",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={col.getIsVisible()}
                        disabled={required}
                        onChange={(e) =>
                          col.toggleVisibility(e.target.checked)
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="h-3.5 w-3.5 rounded border-border-primary accent-brand-primary focus:ring-brand-primary cursor-pointer"
                      />
                      <span>{label}</span>
                      {required && (
                        <span className="ml-auto text-[10px] text-content-tertiary italic">
                          required
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// SECTION_BY_COL_ID lookup for the menu — mirrors columns.tsx but stays in
// this file so the menu doesn't need a new export from columns.
const SECTION_BY_COL_ID: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const id of PRIMARY_COLUMN_IDS) map[id] = "primary";
  for (const id of MARKETING_COLUMN_IDS) map[id] = "marketing";
  for (const id of PRICING_COLUMN_IDS) map[id] = "pricing";
  for (const id of BILLING_COLUMN_IDS) map[id] = "billing";
  for (const id of ENGAGEMENT_COLUMN_IDS) map[id] = "engagement";
  return map;
})();

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CustomerList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useCustomerList();

  const [search, setSearch] = useState("");
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [onlyNeedsReply, setOnlyNeedsReply] = useState(false);
  // Default sort: highest-ARR first. Users can re-sort by clicking column
  // headers; their click overrides this seed via onSortingChange.
  const [sorting, setSorting] = useState<SortingState>([
    { id: "acvArr", desc: true },
  ]);
  const [columnOrder, setColumnOrder] =
    useState<ColumnOrderState>(loadColumnOrder);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    loadColumnVisibility,
  );
  const [acvMode, setAcvMode] = useState<AcvMode>(loadAcvMode);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  // Bulk selection + "mark as test" state. Selection lives outside the table
  // column system (it rides the existing sticky # column via table meta), so
  // the column-order / visibility / sticky logic stays untouched.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Two-step confirm: which bulk action is awaiting confirmation, if any.
  const [pendingBulk, setPendingBulk] = useState<null | "mark" | "unmark">(
    null,
  );

  // Persist column order to localStorage whenever it changes
  useEffect(() => {
    saveColumnOrder(columnOrder);
  }, [columnOrder]);
  useEffect(() => {
    saveColumnVisibility(columnVisibility);
  }, [columnVisibility]);
  useEffect(() => {
    saveAcvMode(acvMode);
  }, [acvMode]);
  // Reset the confirm prompt when the selection empties, so a stale
  // "Confirm" step doesn't reappear the next time rows are selected.
  // bulkError is deliberately NOT cleared here - the success path clears the
  // selection in the same commit it reports a partial failure, and wiping
  // the error along with it would make failures invisible. The error banner
  // lives outside the selection-gated bar and is dismissed explicitly.
  useEffect(() => {
    if (selectedIds.size === 0) {
      setPendingBulk(null);
    }
  }, [selectedIds]);

  /**
   * Every column is always visible — the section chips below are
   * scroll-to-section navigation, not visibility toggles. Clicking
   * "Marketing" scrolls the horizontal table to the first marketing
   * column; same for Pricing / Billing / Engagement.
   */
  const scrollToSection = (sectionKey: VisibleSections) => {
    const idMap: Record<VisibleSections, string | undefined> = {
      primary: PRIMARY_COLUMN_IDS[0],
      marketing: MARKETING_COLUMN_IDS[0],
      pricing: PRICING_COLUMN_IDS[0],
      billing: BILLING_COLUMN_IDS[0],
      engagement: ENGAGEMENT_COLUMN_IDS[0],
    };
    const targetId = idMap[sectionKey];
    if (!targetId) return;
    // Ref-bound (instead of `document.querySelector('.overflow-auto')`) so
    // we don't accidentally scroll an unrelated overflow-auto container —
    // the AskAutumnWidget's right panel also uses overflow-auto.
    const container = tableScrollRef.current;
    if (!container) return;
    // Primary is the leftmost section. Hard-reset scrollLeft to 0 so the
    // first column is fully visible — the sticky-offset math used for
    // other sections collapses to ~0 here, but `scrollTo` with `behavior:
    // smooth` was producing a partial scroll when already near the start.
    if (sectionKey === "primary") {
      container.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const target = container.querySelector<HTMLElement>(
      `th[data-col-id="${targetId}"]`,
    );
    if (!target) return;
    // Sticky-pair total width — # + Hotel headers stay pinned to the left
    // edge, so naive `scrollIntoView({inline: "start"})` lands the section's
    // first column UNDER them. Offset by the sticky width so the section
    // header lands fully visible just to the right of the sticky pair.
    const stickyWidth = Array.from(
      container.querySelectorAll<HTMLElement>(
        'th[data-col-id="rowNumber"], th[data-col-id="hotelName"]',
      ),
    ).reduce((sum, el) => sum + el.offsetWidth, 0);
    const targetLeft = target.offsetLeft;
    container.scrollTo({
      left: Math.max(0, targetLeft - stickyWidth - 8), // 8px breathing room
      behavior: "smooth",
    });
  };

  // Filtered rows: test account toggle + text search + needs-reply toggle
  const filteredRows = useMemo<CustomerRow[]>(() => {
    const rows = data?.rows ?? [];
    return rows.filter((row) => {
      if (!showTestAccounts && row.isTestAccount) return false;
      if (onlyNeedsReply && (row.workload?.needsReplyCount ?? 0) === 0)
        return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = (row.hotelName ?? "").toLowerCase();
        const email = row.primaryEmail.toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [data?.rows, showTestAccounts, onlyNeedsReply, search]);

  // When the user flips the ACV/ARR toggle to "avg3", recompute the headline
  // card by summing the same column the rows display (acvArrAvg3). In default
  // mode we let PortfolioStatCards fall back to the snapshot's totalArr.
  const headlineArrOverride = useMemo<number | undefined>(() => {
    if (acvMode !== "avg3") return undefined;
    // Exclude rows on the $500 / $6,000 placeholder triplet so the headline
    // matches the "excludes CARR estimates" hint shown under the card.
    return filteredRows.reduce((sum, r) => {
      if (r.billing?.revenueIsPlaceholder) return sum;
      return sum + (r.billing?.acvArrAvg3 ?? 0);
    }, 0);
  }, [acvMode, filteredRows]);

  // ---- Bulk selection helpers (drive the # column checkbox via table meta) ----
  const selectableIds = useMemo(
    () => filteredRows.map((r) => r.id),
    [filteredRows],
  );

  // Prune the selection whenever the visible row set changes (search, filter
  // toggles, refetch) so the bulk action can never silently hit rows the
  // admin is no longer looking at. Skipped while a bulk request is in flight:
  // the optimistic patch hides just-marked rows from the filtered view, and
  // pruning then would clear the selection out from under the pending UI.
  useEffect(() => {
    if (bulkPending) return;
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(selectableIds);
      const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableIds, bulkPending]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected =
        selectableIds.length > 0 && selectableIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(selectableIds);
    });
  }, [selectableIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setPendingBulk(null);
    setBulkError(null);
  }, []);

  const runBulkTestFlag = useCallback(
    async (isTest: boolean) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      setBulkPending(true);
      setBulkError(null);
      // Cancel any in-flight list refetch first - otherwise a fetch started
      // before the optimistic write could land after it and resurrect the
      // pre-update rows mid-request (standard TanStack optimistic recipe).
      await queryClient.cancelQueries({ queryKey: CUSTOMER_LIST_QUERY_KEY });
      // Optimistic: flip the flag in the cached rows so they drop out of the
      // (non-test) view immediately.
      queryClient.setQueryData<CustomerListResponse>(
        CUSTOMER_LIST_QUERY_KEY,
        (prev) =>
          prev
            ? {
                ...prev,
                rows: prev.rows.map((r) =>
                  idSet.has(r.id) ? { ...r, isTestAccount: isTest } : r,
                ),
              }
            : prev,
      );
      try {
        const res = await bulkTestFlag(ids, isTest);
        if (!res.ok && (res.failed?.length ?? 0) > 0) {
          setBulkError(
            `${res.failed.length} of ${ids.length} account${ids.length > 1 ? "s" : ""} could not be updated; they remain unchanged in the list.`,
          );
        }
        setSelectedIds(new Set());
        setPendingBulk(null);
      } catch {
        setBulkError("Bulk update failed. Please try again.");
      } finally {
        setBulkPending(false);
        // Reconcile with the server (corrects the optimistic patch either way).
        queryClient.invalidateQueries({ queryKey: CUSTOMER_LIST_QUERY_KEY });
        // The revenue stat cards read a separate query (10-min staleTime) -
        // refetch them too, or just-marked accounts linger in ARR/MRR/CARR.
        // The backend PATCH drops its overview cache before returning, so
        // this refetch recomputes fresh totals on demand.
        queryClient.invalidateQueries({
          queryKey: PORTFOLIO_OVERVIEW_QUERY_KEY,
        });
      }
    },
    [selectedIds, queryClient],
  );

  const table = useReactTable<CustomerRow>({
    data: filteredRows,
    columns: crmColumns,
    state: { sorting, columnOrder, columnVisibility },
    meta: {
      acvMode,
      selectedIds,
      toggleSelect,
      toggleSelectAll,
      selectableIds,
    } satisfies CrmTableMeta,
    onSortingChange: setSorting,
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const computed =
          typeof updater === "function" ? updater(prev) : updater;
        // Defensive copy — never mutate the object handed to us by
        // TanStack Table (it may be the same reference as `prev`, which
        // would short-circuit the React re-render).
        const next = { ...computed };
        // Required columns can never be hidden.
        for (const id of REQUIRED_VISIBLE_COLUMN_IDS) next[id] = true;
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Drag-and-drop column reorder state
  const dragColId = useRef<string | null>(null);

  // Ref to the horizontally-scrolling table container so JUMP-TO chips
  // scroll the right element (rather than scoping by `.overflow-auto`,
  // which the AskAutumnWidget panel also uses).
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, colId: string) => {
      dragColId.current = colId;
      e.dataTransfer.effectAllowed = "move";
    },
    [],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, targetColId: string) => {
      e.preventDefault();
      const from = dragColId.current;
      if (!from || from === targetColId) return;
      setColumnOrder((prev) => {
        const next = [...prev];
        const fromIdx = next.indexOf(from);
        const toIdx = next.indexOf(targetColId);
        if (fromIdx === -1 || toIdx === -1) return prev;
        next.splice(fromIdx, 1);
        next.splice(toIdx, 0, from);
        return next;
      });
      dragColId.current = null;
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    [],
  );

  const totalCount = data?.rows?.length ?? 0;
  const visibleCount = filteredRows.length;

  return (
    <Page>
      <div className="p-4 md:p-6 flex flex-col gap-4 min-h-0 min-w-0 flex-1 lg:h-[100dvh] overflow-hidden bg-white">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-content-primary">
              Customer Dashboard
            </h1>
            {!isLoading && (
              <p className="text-sm text-content-tertiary mt-0.5">
                Showing {visibleCount} of {totalCount} customers
                {data?.fetchedAt && (
                  <span className="ml-2 text-xs">
                    · fetched{" "}
                    {new Date(data.fetchedAt).toLocaleTimeString("en-US", {
                      timeZone: "America/New_York",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </p>
            )}
          </div>
          {/* Refresh button removed — data auto-refreshes via daily cron + on
              page load. Manual refresh was a quota-burning footgun on the
              Google Ads side. Use the per-customer refresh in 360 page if a
              specific customer needs a force-refresh. */}
        </div>

        {/* Portfolio stat cards — 6 CS-leader KPIs above the table */}
        <PortfolioStatCards
          arrOverride={headlineArrOverride}
          arrModeHint={
            acvMode === "avg3"
              ? "Trailing 3-mo avg × 12 · excludes CARR estimates"
              : "Latest month × 12 · excludes CARR estimates"
          }
        />

        {/* Section jump — every column stays visible; these chips scroll
            the table horizontally to the start of the chosen section. */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-content-tertiary uppercase tracking-wide mr-1">
            Jump to:
          </span>
          {(
            [
              { key: "primary", label: "Primary" },
              { key: "marketing", label: "Marketing" },
              { key: "pricing", label: "Pricing" },
              { key: "billing", label: "Billing" },
              { key: "engagement", label: "Engagement" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => scrollToSection(key)}
              className="text-xs px-2.5 py-1 rounded-full border border-[#E9E9EB] bg-surface-primary text-content-secondary hover:bg-brand-primary/5 hover:text-brand-primary hover:border-brand-primary/30 transition-colors inline-flex items-center gap-1"
            >
              <span className="text-content-tertiary">→</span>
              {label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex px-3 py-2 border border-[#E9E9EB] bg-surface-primary rounded-lg items-center gap-2 min-w-[260px]">
            <svg
              className="w-4 h-4 text-gray-400 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by hotel name or email..."
              className="w-full outline-none text-sm bg-transparent"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-content-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showTestAccounts}
              onChange={(e) => setShowTestAccounts(e.target.checked)}
              className="rounded border-border-primary accent-brand-primary"
            />
            Show test accounts
          </label>

          <ColumnsMenu
            table={table}
            open={columnsMenuOpen}
            onToggle={() => setColumnsMenuOpen((v) => !v)}
            onClose={() => setColumnsMenuOpen(false)}
          />

          <button
            type="button"
            onClick={() => setOnlyNeedsReply((v) => !v)}
            className={clsx(
              "text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5",
              onlyNeedsReply
                ? "bg-amber-100 text-amber-800 border-amber-300"
                : "bg-surface-primary text-content-secondary border-[#E9E9EB] hover:bg-surface-hover",
            )}
            title="Show only customers with at least one unreplied inbound thread"
          >
            <span
              className={clsx(
                "w-1.5 h-1.5 rounded-full",
                onlyNeedsReply ? "bg-amber-600" : "bg-content-tertiary",
              )}
            />
            Needs reply
          </button>

          {/* ACV/ARR projection-mode toggle (PRD FR-067) — switches both
              the per-row column and the headline card between
              "latest month projected" (noisy) and "trailing 3-month avg
              projected" (smoother, what the CFO uses for investor decks). */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-content-tertiary">ARR projection:</span>
            <span
              className={clsx(
                "transition-colors",
                acvMode === "default"
                  ? "text-content-primary font-medium"
                  : "text-content-tertiary",
              )}
            >
              Latest month
            </span>
            <Switch
              variant="neutral"
              checked={acvMode === "avg3"}
              onChange={() =>
                setAcvMode((m) => (m === "avg3" ? "default" : "avg3"))
              }
            />
            <span
              className={clsx(
                "transition-colors",
                acvMode === "avg3"
                  ? "text-content-primary font-medium"
                  : "text-content-tertiary",
              )}
            >
              Trailing 3-mo avg
            </span>
            <span
              className="text-content-tertiary cursor-help select-none"
              title={
                acvMode === "avg3"
                  ? "ARR = (avg of last 3 commission bills + monthly fixed fee) × 12. Smooths month-to-month commission volatility — closer to what investors see in the financial deck."
                  : "ARR = (most recent commission bill + monthly fixed fee) × 12. Reflects the latest month as the run rate; can spike/dip with commission volatility."
              }
            >
              ⓘ
            </span>
          </div>

          <button
            type="button"
            onClick={() => exportCustomersToCsv(table)}
            disabled={isLoading || table.getRowModel().rows.length === 0}
            className="ml-auto text-xs px-3 py-1.5 rounded-full border border-[#E9E9EB] bg-surface-primary text-content-secondary hover:bg-brand-primary/5 hover:text-brand-primary hover:border-brand-primary/30 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download the rows and columns currently visible as CSV"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
            Export CSV
          </button>
        </div>

        {/* Bulk action bar - appears once one or more rows are selected via
            the # column checkboxes. "Mark as test" removes them from the CRM
            list + revenue totals (writes both is_test_user and isTestAccount). */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-brand-primary/5 border border-brand-primary/20 rounded-lg">
            <span className="text-sm font-medium text-content-primary">
              {selectedIds.size} selected
            </span>

            {pendingBulk ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-content-secondary">
                  {pendingBulk === "mark"
                    ? `Mark ${selectedIds.size} account${selectedIds.size > 1 ? "s" : ""} as test? They'll be removed from the CRM list, revenue totals, and all automated email/review flows.`
                    : `Unmark ${selectedIds.size} account${selectedIds.size > 1 ? "s" : ""}? They'll return to the CRM list and revenue totals, and re-enter automated email and review-sync flows.`}
                </span>
                <button
                  type="button"
                  disabled={bulkPending}
                  onClick={() => runBulkTestFlag(pendingBulk === "mark")}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-primary text-white font-semibold hover:bg-brand-primary/90 disabled:opacity-50"
                >
                  {bulkPending ? "Working…" : "Confirm"}
                </button>
                <button
                  type="button"
                  disabled={bulkPending}
                  onClick={() => setPendingBulk(null)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E9E9EB] text-content-secondary hover:bg-surface-hover disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPendingBulk("mark")}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-primary text-white font-semibold hover:bg-brand-primary/90"
                >
                  Mark as test
                </button>
                <button
                  type="button"
                  onClick={() => setPendingBulk("unmark")}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E9E9EB] text-content-secondary hover:bg-surface-hover"
                >
                  Unmark test
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-content-tertiary hover:text-content-primary underline"
                >
                  Clear
                </button>
              </div>
            )}

          </div>
        )}

        {/* Bulk failure banner - intentionally OUTSIDE the selection-gated
            bar: the success path clears the selection (unmounting the bar) in
            the same commit a partial failure is reported, so an error inside
            the bar would never be seen. Dismissed explicitly or on next run. */}
        {bulkError && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-brand-secondary/10 border border-brand-secondary/30 rounded-lg text-sm text-brand-secondary">
            <span>{bulkError}</span>
            <button
              type="button"
              onClick={() => setBulkError(null)}
              className="font-medium underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 px-4 py-3 bg-brand-secondary/10 border border-brand-secondary/30 rounded-lg text-sm text-brand-secondary">
            <span>Failed to load customers.</span>
            <button
              onClick={() => refetch()}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        {/* Table wrapper fills the remaining viewport height via flex-1 +
            min-h-0 instead of a hard-coded calc(), so the inner scroll
            container always matches actual available space (last row no
            longer clips when the header area changes height). */}
        <div className="bg-surface-primary rounded-lg shadow-sm border border-border-secondary overflow-hidden min-w-0 flex-1 min-h-0 flex flex-col">
          <div ref={tableScrollRef} className="overflow-auto flex-1 min-h-0">
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="sticky top-0 z-10">
                    {hg.headers.map((header, idx) => {
                      const headers = hg.headers;
                      const align = alignmentClass(header.column.id);
                      const nextColId =
                        idx + 1 < headers.length
                          ? headers[idx + 1].column.id
                          : null;
                      const sectionEnd = isSectionBoundary(
                        header.column.id,
                        nextColId,
                      );
                      const isSortable = header.column.getCanSort();
                      return (
                        <th
                          key={header.id}
                          data-col-id={header.column.id}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, header.column.id)
                          }
                          onDrop={(e) => handleDrop(e, header.column.id)}
                          onDragOver={handleDragOver}
                          className={clsx(
                            "text-[0.8125rem] font-medium text-[#202020] px-4 py-3 bg-[#F4F4F6] whitespace-nowrap",
                            "border-b border-border-secondary",
                            align,
                            isSortable &&
                              "cursor-pointer select-none hover:bg-[#EBEBED]",
                            idx === 0 &&
                              "sticky left-0 z-20 bg-[#F4F4F6] rounded-tl-[8px]",
                            idx === 1 &&
                              "sticky left-12 z-20 bg-[#F4F4F6] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]",
                            sectionEnd &&
                              "border-r-2 border-r-border-secondary",
                          )}
                          onClick={
                            isSortable
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          <span
                            className={clsx(
                              "flex items-center gap-1.5",
                              align === "text-right" && "justify-end",
                              align === "text-center" && "justify-center",
                            )}
                          >
                            {/* Drag handle */}
                            <span
                              className="text-gray-300 text-xs cursor-grab active:cursor-grabbing select-none"
                              title="Drag to reorder"
                            >
                              ⠿
                            </span>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                            {isSortable && (
                              <SortIcon
                                dir={header.column.getIsSorted()}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && (
                  <TableSkeleton
                    rows={8}
                    cols={table.getVisibleLeafColumns().length}
                  />
                )}
                {!isLoading &&
                  table.getRowModel().rows.map((row) => {
                    const band = (row.original.health.band ?? "healthy") as HealthBand;
                    const stickyBg = STICKY_TINT[band];
                    return (
                      <tr
                        key={row.id}
                        className={clsx(
                          "cursor-pointer hover:bg-surface-hover group",
                          ROW_TINT[band],
                        )}
                        onClick={() =>
                          navigate(`/crm/customers/${row.original.id}`)
                        }
                      >
                        {row.getVisibleCells().map((cell, cidx, cells) => {
                          const align = alignmentClass(cell.column.id);
                          const nextColId =
                            cidx + 1 < cells.length
                              ? cells[cidx + 1].column.id
                              : null;
                          const sectionEnd = isSectionBoundary(
                            cell.column.id,
                            nextColId,
                          );
                          // Products cell wraps capsules onto multiple lines
                          // when a customer has 4–5; default whitespace-nowrap
                          // would force them into a horizontal scroll.
                          const allowWrap = cell.column.id === "products";
                          return (
                            <td
                              key={cell.id}
                              className={clsx(
                                "px-4 py-3 border-b border-b-border-primary text-[0.8125rem] align-middle",
                                allowWrap ? "whitespace-normal" : "whitespace-nowrap",
                                align,
                                cidx === 0 &&
                                  clsx(
                                    "sticky left-0 z-[5] group-hover:bg-surface-hover",
                                    stickyBg,
                                  ),
                                cidx === 1 &&
                                  clsx(
                                    "sticky left-12 z-[5] group-hover:bg-surface-hover shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]",
                                    stickyBg,
                                  ),
                                sectionEnd &&
                                  "border-r-2 border-r-border-secondary",
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {/* Empty state */}
            {!isLoading && !isError && filteredRows.length === 0 && (
              <div className="flex flex-col justify-center items-center py-16">
                <span className="text-content-tertiary text-base font-medium">
                  No customers yet
                </span>
                {search && (
                  <span className="text-[#bbbbbb] text-sm mt-1">
                    Try clearing the search filter
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
